import React, { useState } from 'react';
import { ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { evaluationApi, EvaluationResult } from '../../services/api';

export function ResultTable({ result, evaluationId, reload }: { result: EvaluationResult; evaluationId: string; reload: () => void }) {
  const criteria = result.matrix.criteria || [];
  const targets = result.matrix.targets || [];
  const scores = result.matrix.scores || {};
  const summaries = Object.fromEntries((result.matrix.summaries || []).map((s: any) => [s.target_id, s]));
  const includedEngines: string[] = result.matrix.engines || [];
  const skippedEngines: { provider: string; reason: string }[] = result.matrix.skipped_engines || [];
  const isAggregate = Boolean(result.matrix.aggregate) || result.engine_provider === 'Aggregate';
  const pending = result.status === 'running' || Boolean(result.matrix?.pending);
  const failed = result.status === 'failed' || Boolean(result.matrix?.error);
  const errorMessage = String(result.matrix?.error || 'The evaluation failed to complete. Try running it again.');
  const [actionError, setActionError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const iconForScore = (score: number) => score === 2 ? '🟢' : score === 1 ? '⚠️' : '❌';
  const bandForAverage = (average: number) => Math.min(2, Math.max(0, Math.floor(average + 0.5)));
  const summaryForTarget = (target: any) => {
    const targetScores = scores[target.id] || {};
    const values = criteria
      .map((criterion: any) => targetScores[criterion.id]?.score)
      .filter((score: any) => typeof score === 'number');
    const average = values.length ? Number((values.reduce((sum: number, score: number) => sum + score, 0) / values.length).toFixed(1)) : 0;
    const band = bandForAverage(average);
    return {
      ...(summaries[target.id] || {}),
      average,
      score_band: band,
      icon: iconForScore(band),
    };
  };
  const deleteResult = async () => {
    const confirmed = window.confirm(`Delete the ${result.engine_provider} · ${result.engine_model} result?`);
    if (!confirmed) return;
    setActionError('');
    try {
      await evaluationApi.deleteResult(evaluationId, result.id);
      reload();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setActionError(typeof detail === 'string' ? detail : err.message || 'Could not delete result.');
    }
  };
  const downloadResult = async () => {
    setActionError('');
    setDownloading(true);
    try {
      const safeProvider = (result.engine_provider || 'engine').replace(/[^a-z0-9_-]+/gi, '_');
      const safeModel = (result.engine_model || 'model').replace(/[^a-z0-9_.-]+/gi, '_');
      await evaluationApi.exportResult(evaluationId, result.id, `vendor_review_${safeProvider}_${safeModel}.xlsx`);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setActionError(typeof detail === 'string' ? detail : err.message || 'Could not download XLSX.');
    } finally {
      setDownloading(false);
    }
  };
  // In-progress or failed runs skip the score table and show a compact status card
  // (the run is processed by a background job; the workspace polls until it settles).
  if (pending || failed) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{result.engine_provider} · {result.engine_model}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{new Date(result.created_at).toLocaleString()}</p>
          </div>
          <button onClick={deleteResult} className="inline-flex items-center px-3 py-2 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-700 dark:text-red-300 text-sm rounded-lg"><TrashIcon className="w-4 h-4 mr-1" /> Delete</button>
        </div>
        {pending ? (
          <div className="p-6 flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
            <svg className="animate-spin w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            Evaluation in progress… this can take a minute or two for large runs. Results appear here automatically.
          </div>
        ) : (
          <div className="p-6 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20">{errorMessage}</div>
        )}
        {actionError && <div className="mx-4 mb-4 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">{actionError}</div>}
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{result.engine_provider} · {result.engine_model}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">{new Date(result.created_at).toLocaleString()}</p>
        </div>
        <div className="flex items-center gap-2">
          <button disabled={downloading} onClick={downloadResult} className="inline-flex items-center px-3 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-60 text-gray-700 dark:text-gray-300 text-sm rounded-lg"><ArrowDownTrayIcon className="w-4 h-4 mr-1" /> {downloading ? 'Downloading...' : 'XLSX'}</button>
          <button onClick={deleteResult} className="inline-flex items-center px-3 py-2 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-700 dark:text-red-300 text-sm rounded-lg"><TrashIcon className="w-4 h-4 mr-1" /> Delete</button>
        </div>
      </div>
      {isAggregate && (includedEngines.length > 0 || skippedEngines.length > 0) && (
        <div className="mx-4 mt-3 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-3 space-y-1">
          {includedEngines.length > 0 && (
            <div className="text-gray-600 dark:text-gray-400"><span className="font-medium text-gray-700 dark:text-gray-300">Aggregated {includedEngines.length} engine{includedEngines.length !== 1 ? 's' : ''}:</span> {includedEngines.join(', ')}</div>
          )}
          {skippedEngines.length > 0 && (
            <div className="text-amber-700 dark:text-amber-400"><span className="font-medium">Skipped:</span> {skippedEngines.map((s) => `${s.provider} (${s.reason})`).join('; ')}</div>
          )}
        </div>
      )}
      {actionError && <div className="mx-4 mt-3 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">{actionError}</div>}
      <div className="overflow-x-auto bg-white dark:bg-gray-800">
        <table className="min-w-full text-xs bg-white dark:bg-gray-800">
          <thead className="bg-slate-800 text-white">
            <tr>
              <th className="sticky left-0 z-20 bg-slate-800 border-r border-slate-700 p-3 text-left min-w-48">Target</th>
              {criteria.map((c: any) => <th key={c.id} className="p-3 text-left min-w-56">{c.name}</th>)}
              <th className="p-3 text-left min-w-72">Scoring</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {targets.map((target: any) => {
              const summary = summaryForTarget(target);
              const summaryFill = summary.score_band === 2 ? 'bg-green-50 dark:bg-green-900/20' : summary.score_band === 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20';
              return (
                <tr key={target.id} className="align-top">
                  <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 p-3 font-semibold text-gray-900 dark:text-white">{target.product_name}<div className="font-normal text-gray-500">{target.vendor}</div></td>
                  {criteria.map((criterion: any) => {
                    const entry = scores[target.id]?.[criterion.id] || {};
                    const fill = entry.score === 2 ? 'bg-green-50 dark:bg-green-900/20' : entry.score === 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20';
                    return <td key={criterion.id} className={`p-3 ${fill}`}><div className="text-center text-base">{entry.icon}</div><div className="mt-2 text-gray-700 dark:text-gray-300">{entry.notes}</div></td>;
                  })}
                  <td className={`p-3 ${summaryFill} text-gray-700 dark:text-gray-300`}><div className="font-semibold">{summary.icon} {summary.average}</div><div className="mt-2">Pros: {summary.pros}</div><div>Cons: {summary.cons}</div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
